<template>
  <default-view-component
    :showAlertCard="showAlertCard"
    :alertCardData="alertCardData"
    @hideAlertCard="showAlertCard = !showAlertCard"
    :darkMode="darkMode"
  >
    <template v-slot:content>
      <div class="container">
        <div v-if="isSuccess" class="content">
          <default-section-component
            class="default-section-component"
            title="Contrato criado com sucesso!"
            :darkMode="darkMode"
          >
            <template v-slot:content>
              <div class="success-container" :class="{ 'dark-mode': darkMode }">
                <div class="success-message">
                  <p class="contract-id">
                    Contrato nº
                    <span>{{ contractCreatedData?.Codigo }}</span>
                  </p>

                  <div class="qr-code-section">
                    <p class="qr-code-label">
                      Escaneie o QR Code abaixo para assinar o contrato:
                    </p>

                    <div
                      v-if="contractCreatedData?.qrCode"
                      class="qr-code-container"
                    >
                      <img
                        :src="contractCreatedData.qrCode"
                        alt="QR Code para assinatura do contrato"
                        class="qr-code-image"
                      />
                    </div>

                    <p class="qr-code-hint">
                      O aluno pode escanear o código e assinar imediatamente no
                      celular.
                    </p>
                  </div>
                </div>
              </div>
            </template>
          </default-section-component>

          <div class="button-container success-buttons">
            <button-component
              text="Novo Contrato"
              class="button-component"
              v-ripple="'rgba(255, 255, 255, 0.35)'"
              styles="secondary"
              :isDisabled="hasRequest"
              @click.native="onStartNewContract()"
              :darkMode="darkMode"
            />

            <button-component
              text="Ir para Pagamento"
              class="button-component left"
              v-ripple="'rgba(255, 255, 255, 0.35)'"
              :hasLoader="hasRequest"
              :isDisabled="hasRequest"
              @click.native="onPaymentClick()"
              :darkMode="darkMode"
            />
          </div>
        </div>

        <div v-else-if="!isSummary" class="content">
          <ValidationObserver
            class="form-container"
            tag="div"
            ref="form"
            v-slot="{ passes }"
          >
            <form @submit.prevent="passes(submit)">
              <default-section-component
                class="default-section-component"
                title="Informações do contrato"
                :darkMode="darkMode"
              >
                <template v-slot:content>
                  <div class="field-container">
                    <div class="section">
                      <input-field
                        class="input-field"
                        label="CPF"
                        mask="###.###.###-##"
                        :hasButtonCleaner="true"
                        rules="required|cpf"
                        :isDisabled="hasRequest"
                        :darkMode="darkMode"
                        inputMode="tel"
                        v-model="data.taxpayerId"
                      />

                      <input-field
                        class="input-field"
                        label="Nome completo"
                        :hasButtonCleaner="true"
                        rules="required|completedName"
                        :isDisabled="hasRequest || disableNameField"
                        :darkMode="darkMode"
                        v-model="data.name"
                      />

                      <input-field
                        class="input-field"
                        label="E-mail"
                        :hasButtonCleaner="true"
                        rules="required|email"
                        :isDisabled="hasRequest || disableEmailField"
                        :darkMode="darkMode"
                        inputMode="email"
                        v-model="data.email"
                      />

                      <input-field
                        class="input-field"
                        label="Telefone"
                        :mask="['(##) #####-####', '(##) ####-####']"
                        :hasButtonCleaner="true"
                        rules="required|phone"
                        :isDisabled="hasRequest || disablePhoneField"
                        :darkMode="darkMode"
                        inputMode="tel"
                        v-model="data.phone"
                      />

                      <input-field
                        class="input-field"
                        label="Data de nascimento"
                        type="date"
                        :maxCalendarDate="new Date()"
                        :hasButtonCleaner="true"
                        :isDisabled="false"
                        :darkMode="darkMode"
                        inputMode="numeric"
                        v-model="data.birthDate"
                      />

                      <input-field
                        class="input-field"
                        label="RG"
                        :hasButtonCleaner="true"
                        :isDisabled="hasRequest"
                        :darkMode="darkMode"
                        inputMode="tel"
                        v-model="data.document"
                      />

                      <input-field
                        class="input-field"
                        ref="postalCode"
                        label="CEP"
                        mask="#####-###"
                        rules="postalCode"
                        :hasButtonCleaner="true"
                        :isDisabled="hasRequest"
                        :darkMode="darkMode"
                        inputMode="tel"
                        v-model="data.postalCode"
                      />
                    </div>

                    <div class="section">
                      <checkbox-component
                        v-model="data.isResponsible"
                        label="Não é aluno"
                        :isDisabled="hasRequest"
                        :darkMode="darkMode"
                      />

                      <div v-if="data.isResponsible">
                        <input-field
                          class="input-field"
                          label="CPF"
                          mask="###.###.###-##"
                          :hasButtonCleaner="true"
                          rules="required|cpf"
                          :isDisabled="hasRequest"
                          :darkMode="darkMode"
                          inputMode="tel"
                          v-model="data.student.taxpayerId"
                        />

                        <input-field
                          class="input-field"
                          label="Nome completo do aluno"
                          :hasButtonCleaner="true"
                          rules="required|completedName"
                          :isDisabled="hasRequest || disableStudentNameField"
                          :darkMode="darkMode"
                          v-model="data.student.name"
                        />

                        <input-field
                          class="input-field"
                          label="Data de nascimento"
                          type="date"
                          :maxCalendarDate="new Date()"
                          :hasButtonCleaner="true"
                          :isDisabled="false"
                          :darkMode="darkMode"
                          inputMode="numeric"
                          v-model="data.student.birthDate"
                        />

                        <input-field
                          class="input-field"
                          label="RG"
                          :hasButtonCleaner="true"
                          :isDisabled="hasRequest"
                          :darkMode="darkMode"
                          inputMode="tel"
                          v-model="data.student.document"
                        />
                      </div>
                    </div>

                    <div class="section">
                      <p :class="{ 'dark-mode': darkMode }">
                        Matrícula / Entrada / Sinal
                      </p>

                      <input-field
                        class="input-field"
                        label="Valor"
                        :maxLength="11"
                        type="money"
                        :rules="
                          mountInputMoneyRules(
                            'minValue',
                            data.amountEnrollment,
                            0
                          )
                        "
                        :hasButtonCleaner="true"
                        :isDisabled="hasRequest"
                        :darkMode="darkMode"
                        inputMode="decimal"
                        v-model="data.amountEnrollment"
                      />

                      <input-field
                        class="input-field"
                        label="Data vencimento"
                        type="date"
                        :minCalendarDate="new Date()"
                        :hasButtonCleaner="true"
                        rules="required|date"
                        :isDisabled="hasRequest"
                        :darkMode="darkMode"
                        inputMode="numeric"
                        v-model="data.dueDate"
                      />
                    </div>

                    <div class="section">
                      <checkbox-component
                        v-model="data.hasSignal"
                        label="Complemento de matrícula"
                        :isDisabled="hasRequest"
                        :darkMode="darkMode"
                      />

                      <div v-if="data.hasSignal">
                        <input-field
                          class="input-field"
                          label="Valor"
                          :maxLength="11"
                          type="money"
                          :rules="
                            mountInputMoneyRules(
                              'maxValue',
                              data.amountEnrollment,
                              data.signal.amount
                            )
                          "
                          :hasButtonCleaner="true"
                          :isDisabled="hasRequest"
                          :darkMode="darkMode"
                          inputMode="decimal"
                          v-model="data.signal.amount"
                        />

                        <input-field
                          class="input-field"
                          label="Data vencimento"
                          type="date"
                          :minCalendarDate="new Date()"
                          :hasButtonCleaner="true"
                          rules="required|date"
                          :isDisabled="hasRequest"
                          :darkMode="darkMode"
                          inputMode="numeric"
                          v-model="data.signal.dueDate"
                        />
                      </div>
                    </div>

                    <div class="section">
                      <p :class="{ 'dark-mode': darkMode }">Curso(s)</p>

                      <select-field
                        v-if="getIsMultipleFranchise"
                        class="input-field"
                        label="Franquia"
                        rules="required"
                        :option="getFranchisesCollaborator"
                        v-model="data.franchiseId"
                        :isDisabled="hasRequest"
                        :darkMode="darkMode"
                      />

                      <input-field
                        v-else
                        class="input-field"
                        label="Franquia"
                        rules="required"
                        :value="getFranchiseLabel(data.franchiseId)"
                        :isDisabled="true"
                      />

                      <div
                        v-if="showModality"
                        class="toggle-card-wrapper"
                        :class="{ 'dark-mode': darkMode }"
                      >
                        <div class="toggle-card">
                          <div
                            v-for="(item, index) in modalityCardData"
                            :key="index"
                            class="card"
                            :class="{
                              active: item.isActive,
                              disable: item.isDisabled || hasRequest,
                            }"
                            @click="toggleModality(item.value)"
                          >
                            {{ item.label }}
                          </div>
                        </div>
                      </div>

                      <select-field
                        class="input-field"
                        label="Mídia"
                        :option="medias"
                        v-model="data.mediaId"
                        :isDisabled="
                          hasRequest || !data.franchiseId || medias.length == 0
                        "
                        :darkMode="darkMode"
                      />

                      <select-field
                        class="input-field"
                        label="Tipo de atendimento"
                        :option="serviceTypes"
                        v-model="data.serviceTypeId"
                        :isDisabled="
                          hasRequest ||
                          !data.mediaId ||
                          serviceTypes.length == 0
                        "
                        :darkMode="darkMode"
                      />

                      <div
                        v-for="(item, index) in data.courses"
                        :key="`${item.id}-${index}`"
                        class="course-item"
                      >
                        <select-field
                          v-if="!isDigital()"
                          class="input-field"
                          label="Curso"
                          rules="required"
                          :option="franchiseGenericCourses"
                          :isDisabled="
                            !(franchiseGenericCourses.length > 0) || hasRequest
                          "
                          v-model="item.genericId"
                          @input="formatFranchiseCourses(item)"
                          :darkMode="darkMode"
                        />

                        <div class="course-version-container">
                          <select-field
                            class="input-field"
                            :label="isDigital() ? 'Curso' : 'Versão'"
                            rules="required"
                            :value="item.id"
                            :option="
                              isDigital()
                                ? franchiseDigitalCourses
                                : item.franchiseNormalCourses
                            "
                            :isDisabled="
                              (!(franchiseDigitalCourses.length > 0) &&
                                !(item.franchiseNormalCourses.length > 0)) ||
                              hasRequest
                            "
                            v-model="item.id"
                            :darkMode="darkMode"
                          />

                          <button
                            v-if="index != 0"
                            class="remove-course-btn"
                            @click="removeCourse(index)"
                          >
                            <i class="icon-remove" />
                          </button>
                        </div>
                      </div>

                      <button-component
                        v-if="data.courses.length < 4"
                        text="Adicionar"
                        styles="ghost"
                        v-ripple="'rgba(255, 255, 255, 0.2)'"
                        icon-left="icon-add"
                        type="button"
                        :style="{
                          'margin-bottom': '2.5rem',
                          'max-width': '150px',
                        }"
                        :hasLoader="hasRequest"
                        :isDisabled="hasCourseIdEmpty() || hasRequest"
                        @click.native="addCourse"
                        :darkMode="darkMode"
                      />

                      <div v-if="!isDigital()">
                        <div class="row">
                          <input-field
                            class="input-field"
                            label="Data início"
                            type="date"
                            :hasButtonCleaner="true"
                            :isDisabled="
                              !hasSomeCourseIdSelected() || hasRequest
                            "
                            rules="required|date"
                            v-model="data.startDate"
                            :darkMode="darkMode"
                            inputMode="numeric"
                          />

                          <input-field
                            class="input-field"
                            ref="endDateField"
                            label="Data fim"
                            type="date"
                            :hasButtonCleaner="true"
                            :isDisabled="disableEndDate || hasRequest"
                            rules="required|date"
                            v-model="data.endDate"
                            :darkMode="darkMode"
                            inputMode="numeric"
                          />
                        </div>

                        <div class="row">
                          <select-field
                            class="input-field"
                            label="Horário início"
                            rules="required"
                            :option="startTimesArr"
                            :isDisabled="disableStartTime || hasRequest"
                            v-model="data.startTime"
                            :darkMode="darkMode"
                          />

                          <select-field
                            class="input-field"
                            label="Horário fim"
                            rules="required"
                            :option="endTimesArr"
                            :isDisabled="disableEndTime || hasRequest"
                            v-model="data.endTime"
                            :darkMode="darkMode"
                          />
                        </div>
                      </div>
                    </div>

                    <div class="section">
                      <p :class="{ 'dark-mode': darkMode }">Parcelas</p>

                      <input-field
                        class="input-field"
                        label="Valor"
                        :maxLength="11"
                        type="money"
                        rules="required"
                        :hasButtonCleaner="true"
                        :isDisabled="hasRequest"
                        :darkMode="darkMode"
                        inputMode="decimal"
                        v-model="data.payment.amount"
                      />

                      <input-field
                        class="input-field"
                        ref="courseDiscountField"
                        label="Valor c/ desc."
                        :maxLength="11"
                        type="money"
                        :rules="
                          mountInputMoneyRules(
                            'maxValue',
                            data.payment.amount,
                            data.payment.amountWithDiscount
                          )
                        "
                        :hasButtonCleaner="true"
                        :isDisabled="
                          hasRequest || disableCourseDiscountAmountField
                        "
                        :darkMode="darkMode"
                        inputMode="decimal"
                        v-model="data.payment.amountWithDiscount"
                      />

                      <input-field
                        class="input-field"
                        label="Data vencimento"
                        type="date"
                        :minCalendarDate="new Date()"
                        :hasButtonCleaner="true"
                        rules="required|date"
                        :isDisabled="hasRequest"
                        :darkMode="darkMode"
                        inputMode="numeric"
                        v-model="data.payment.dueDate"
                      />

                      <range-field
                        class="range-field"
                        label="Quantidade de parcelas"
                        :secondLabel="
                          data.payment.amountWithDiscount
                            ? totalCourseAmount
                            : ''
                        "
                        :min="1"
                        :max="30"
                        :initialValue="data.payment.installments"
                        :isDisabled="hasRequest || disableCourseInstallment"
                        v-model="data.payment.installments"
                      />
                    </div>

                    <div class="section">
                      <checkbox-component
                        label="Material didático"
                        v-model="data.isCourseware"
                        :isDisabled="hasRequest"
                        :darkMode="darkMode"
                      />

                      <div v-if="data.isCourseware">
                        <input-field
                          class="input-field"
                          label="Valor"
                          :maxLength="11"
                          type="money"
                          rules="required"
                          :hasButtonCleaner="true"
                          :isDisabled="hasRequest"
                          :darkMode="darkMode"
                          inputMode="decimal"
                          v-model="data.courseware.amount"
                        />

                        <input-field
                          class="input-field"
                          ref="coursewareDiscountField"
                          label="Valor c/ desc."
                          :maxLength="11"
                          type="money"
                          :rules="
                            mountInputMoneyRules(
                              'maxValue',
                              data.courseware.amount,
                              data.courseware.amountWithDiscount
                            )
                          "
                          :hasButtonCleaner="true"
                          :isDisabled="
                            hasRequest || disableCoursewareDiscountAmountField
                          "
                          :darkMode="darkMode"
                          inputMode="decimal"
                          v-model="data.courseware.amountWithDiscount"
                        />

                        <input-field
                          class="input-field"
                          label="Data vencimento"
                          type="date"
                          :minCalendarDate="new Date()"
                          :hasButtonCleaner="true"
                          rules="required|date"
                          :isDisabled="hasRequest"
                          :darkMode="darkMode"
                          inputMode="numeric"
                          v-model="data.courseware.dueDate"
                        />

                        <range-field
                          class="range-field"
                          label="Quantidade de parcelas"
                          :secondLabel="
                            data.courseware.amountWithDiscount
                              ? totalCoursewareAmount
                              : ''
                          "
                          :min="1"
                          :max="30"
                          :initialValue="data.courseware.installments"
                          :isDisabled="
                            hasRequest || disableCoursewareInstallment
                          "
                          v-model="data.courseware.installments"
                        />
                      </div>
                    </div>
                  </div>
                </template>
              </default-section-component>

              <div class="button-container">
                <button-component
                  class="button-component left"
                  text="Próximo"
                  v-ripple="'rgba(255, 255, 255, 0.35)'"
                  :hasLoader="hasRequest"
                  :isDisabled="hasRequest"
                  :darkMode="darkMode"
                />
              </div>
            </form>
          </ValidationObserver>
        </div>

        <div v-else-if="isSummary" class="content">
          <default-section-component
            class="default-section-component"
            title="Resumo do contrato"
            :darkMode="darkMode"
          >
            <template v-slot:content>
              <div class="summary-container" :class="{ 'dark-mode': darkMode }">
                <div class="summary-section">
                  <div class="item">
                    <p class="label">Aluno</p>

                    <p class="value">
                      {{
                        data.isResponsible
                          ? `${
                              data.student.name +
                              ', CPF ' +
                              data.student.taxpayerId
                            }`
                          : `${data.name + ', CPF ' + data.taxpayerId}`
                      }}
                    </p>
                  </div>
                </div>

                <div class="summary-section">
                  <div class="item">
                    <p class="label">Modalidade</p>

                    <p class="value">
                      {{ isDigital() ? 'Digital' : 'Presencial' }}
                    </p>
                  </div>
                </div>

                <div class="summary-section">
                  <div class="item">
                    <p class="label">Mídia</p>

                    <p class="value">
                      {{ getMediaLabel(data.mediaId) }}
                    </p>
                  </div>
                </div>

                <div class="summary-section">
                  <div class="item">
                    <p class="label">Tipo de atendimento</p>

                    <p class="value">
                      {{ getServiceTypeLabel(data.serviceTypeId) }}
                    </p>
                  </div>
                </div>

                <div class="summary-section">
                  <div
                    v-for="(item, index) in getSelectedCoursesDetail()"
                    :key="index"
                    class="item"
                  >
                    <p v-if="index == 0" class="label">Curso(s)</p>

                    <p class="value">
                      {{ item.label }}
                    </p>
                  </div>

                  <div v-if="!isDigital()" class="item">
                    <p class="label">Duração</p>

                    <p class="value">
                      {{ $service.dateFormat(data.startDate, 'DD/MM/YYYY') }} -
                      {{ $service.dateFormat(data.endDate, 'DD/MM/YYYY') }}
                    </p>
                  </div>

                  <div v-if="!isDigital()" class="item">
                    <p class="label">Horários</p>

                    <p class="value">
                      {{ parseTime(data.startTime) }} -
                      {{ parseTime(data.endTime) }}
                    </p>
                  </div>
                </div>

                <div class="summary-section">
                  <div class="item">
                    <p class="label">Matrícula / Entrada / Sinal</p>

                    <p class="value">
                      {{
                        $service.formatBrlAmountSymbol(data.amountEnrollment)
                      }}
                    </p>
                  </div>

                  <div v-if="data.hasSignal" class="item">
                    <p class="label">Complemento de matrícula</p>

                    <p class="value">
                      {{ $service.formatBrlAmountSymbol(data.signal.amount) }}
                    </p>
                  </div>

                  <div v-if="data.isCourseware" class="item">
                    <p class="label">Material didático</p>

                    <p class="value">
                      {{
                        `${
                          data.courseware.installments +
                          'x de ' +
                          $service.formatBrlAmountSymbol(
                            data.courseware.amountWithDiscount
                          )
                        }`
                      }}
                    </p>
                  </div>

                  <div class="item">
                    <p class="label">Parcela</p>

                    <p class="value">
                      {{
                        `${
                          data.payment.installments +
                          'x de ' +
                          $service.formatBrlAmountSymbol(
                            data.payment.amountWithDiscount
                          )
                        }`
                      }}
                    </p>
                  </div>

                  <div class="divider"></div>

                  <div class="item">
                    <p class="label">Total do contrato</p>

                    <p class="value">
                      {{
                        $service.formatBrlAmountSymbol(parseTotalCourseAmount())
                      }}
                    </p>
                  </div>

                  <div class="item">
                    <p class="label">Saldo a pagar</p>

                    <p class="value">
                      {{
                        $service.formatBrlAmountSymbol(parseDebitCourseAmount())
                      }}
                    </p>
                  </div>
                </div>
              </div>
            </template>
          </default-section-component>

          <div class="button-container">
            <button-component
              text="Voltar"
              class="button-component"
              v-ripple="'rgba(255, 255, 255, 0.35)'"
              styles="secondary"
              iconLeft="icon-arrow-left"
              :isDisabled="hasRequest"
              @click.native="isSummary = !isSummary"
              :darkMode="darkMode"
            />

            <button-component
              text="Finalizar"
              class="button-component left"
              v-ripple="'rgba(255, 255, 255, 0.35)'"
              :hasLoader="hasRequest"
              :isDisabled="hasRequest"
              @click.native="createCheckout()"
              :darkMode="darkMode"
            />
          </div>
        </div>
      </div>
    </template>
  </default-view-component>
</template>

<script>
import 'moment/locale/pt-br';
import moment from 'moment';
import QRCode from 'qrcode';
import { mapGetters } from 'vuex';
import { ValidationObserver } from 'vee-validate';
import InputField from '@/components/pos/InputField';
import DefaultSection from '@/components/pos/base/DefaultSection';
import DefaultView from '@/components/pos/base/DefaultView';
import Button from '@/components/pos/Button';
import SelectField from '@/components/pos/SelectField';
import Checkbox from '@/components/pos/Checkbox';
import RangeField from '@/components/pos/RangeField';

export default {
  name: 'NewContractPage',

  data() {
    return {
      hasRequest: false,
      hasPostalCodeRequest: false,
      hasFranchiseSelected: false,

      isSummary: false,

      showAlertCard: false,
      alertCardData: {},

      showModality: false,

      modalityCardData: [
        {
          label: 'Presencial',
          isActive: true,
          isDisabled: false,
          value: 'normal',
        },

        {
          label: 'IM Digital',
          isActive: false,
          isDisabled: false,
          value: 'digital',
        },
      ],

      franchiseCourseDay: [
        {
          label: 'Domingo',
          value: 0,
        },

        {
          label: 'Segunda-feira',
          value: 1,
        },

        {
          label: 'Terça-feira',
          value: 2,
        },

        {
          label: 'Quarta-feira',
          value: 3,
        },

        {
          label: 'Quinta-feira',
          value: 4,
        },

        {
          label: 'Sexta-feira',
          value: 5,
        },

        {
          label: 'Sábado',
          value: 6,
        },
      ],

      data: {
        name: '',
        phone: '',
        email: '',
        taxpayerId: '',
        document: '',
        birthDate: '',

        amountEnrollment: '',
        dueDate: '',

        postalCode: '',
        address: this.initialAddress(),

        franchiseId: '',

        mediaId: '',

        serviceTypeId: '',

        isDigital: false,

        startWeekday: '',
        startDate: '',
        endDate: '',
        startTime: '',
        endTime: '',

        courses: [
          {
            genericId: '',
            id: '',
            franchiseNormalCourses: [],
          },
        ],

        isTotalPayment: false,
        payment: {
          amount: '',
          amountWithDiscount: '',
          dueDate: '',
          installments: 1,
        },

        isResponsible: false,
        student: {
          taxpayerId: '',
          name: '',
          document: '',
          birthDate: '',
        },

        hasSignal: false,
        signal: {
          amount: '',
          dueDate: '',
        },

        isCourseware: false,
        courseware: {
          amount: '',
          amountWithDiscount: '',
          dueDate: '',
          installments: 1,
        },
      },

      franchiseDigitalCourses: [],
      franchiseGenericCourses: [],

      medias: [],

      serviceTypes: [],

      disableWeekday: true,

      disableStartDate: true,
      disableEndDate: true,

      startTimesArr: [],
      disableStartTime: true,

      endTimesArr: [],
      disableEndTime: true,

      disableCourseInstallment: true,
      disableCoursewareInstallment: true,
      disableCourseDiscountAmountField: true,
      disableCoursewareDiscountAmountField: true,

      disableEmailField: true,
      disableNameField: true,
      disablePhoneField: true,

      disableStudentNameField: true,

      totalCourseAmount: '',
      totalCoursewareAmount: '',

      referenceId: '',
      darkMode: false,

      isSuccess: false,
      contractCreatedData: null,
    };
  },

  computed: {
    ...mapGetters({
      getIsMultipleFranchise: 'getIsMultipleFranchise',
      getFranchisesCollaborator: 'getFranchisesCollaborator',
      getCollaboratorId: 'getCollaboratorId',
      getIsExternalAuth: 'authModule/getIsExternalAuth',
    }),
  },

  components: {
    ValidationObserver,
    'input-field': InputField,
    'select-field': SelectField,
    'default-section-component': DefaultSection,
    'default-view-component': DefaultView,
    'checkbox-component': Checkbox,
    'button-component': Button,
    'range-field': RangeField,
  },

  created() {
    this.referenceId = this.$route.params.referenceId;

    const darkModeParam = this.$route.query?.darkMode;
    this.darkMode =
      typeof darkModeParam === 'boolean'
        ? darkModeParam
        : this.$service.stringToBoolean(darkModeParam);

    this.data.isResponsible = this.$service.stringToBoolean(
      this.$route.params?.isResponsible
    );
    this.data.student.name = this.$route.params.studentName;
    this.data.student.taxpayerId = this.$route.params.studentTaxpayerId;

    this.data.postalCode = this.$route.params.postalCode;
    this.data.taxpayerId = this.$route.params.taxpayerId;

    if (this.$route.params?.franchiseId) {
      var items = this.getFranchisesCollaborator.filter(
        (item) => item.value == this.$route.params.franchiseId
      );

      this.data.franchiseId =
        items.length == 1 ? this.$route.params.franchiseId : '';
    }

    if (this.getIsExternalAuth && !this.$route.params?.fistExternalLogin) {
      this.$store.dispatch('logout').then(() => {
        this.$router.push(this.$router.history._startLocation);
      });
    }

    this.disableToggleCards();
  },

  mounted() {
    if (
      !this.getIsMultipleFranchise &&
      this.getFranchisesCollaborator.length > 0
    ) {
      this.data.franchiseId = this.getFranchisesCollaborator[0].value;
    }

    this.fetchMedias();
    this.fetchServiceTypes();

    // Delay para garantir que DOM está renderizado antes de emitir evento
    setTimeout(() => {
      try {
        const username = localStorage.getItem('username') || 'unknown';
        this.$androidService.sendMessage('im-contract-loaded-successfully', {
          screen: 'newPOS',
          username: username,
          userColaborador: this.getCollaboratorId,
          hasMultipleFranchises: this.getIsMultipleFranchise,
          franchisesCount: this.getFranchisesCollaborator.length,
          readyForInput: true,
          darkMode: this.darkMode,
        });
      } catch (error) {
        // Falha silenciosa em evento de telemetria
      }
    }, 300); // 300ms de delay para garantir renderização
  },

  watch: {
    'data.postalCode'(value) {
      if (value && this.$service.removeSpecialCharacter(value).length == 8) {
        this.fetchPostalCode();
      }
    },

    'data.taxpayerId'(value) {
      if (this.hasTaxpayerUserFetch(value)) {
        this.$store
          .dispatch('getPersonByTaxpayerId', {
            document: value,
          })
          .then((res) => {
            this.data.name = res.person.NomePessoaNomeFantasia ?? '';
            this.data.phone = this.$service.removeSpecialCharacter(
              res.person.TelefoneCelular
            );
            this.data.email = res.person.Email ?? '';
            this.data.birthDate =
              moment(res.person.DataNascDataAbertura, 'YYYY-MM-DD').toDate() ??
              '';
            this.data.document = res.person.RGouIE ?? '';
          })
          .catch(() => {
            this.resetResponsibleData();

            this.data.name = this.$route.params?.name ?? '';
            this.data.phone = this.$route.params?.phone ?? '';
            this.data.email = this.$route.params?.email ?? '';
            this.data.birthDate =
              moment(this.$route.params?.birthDate, 'YYYY-MM-DD').toDate() ??
              '';
            this.data.document = this.$route.params?.document ?? '';

            this.disableNameField = false;
          })
          .finally(() => {
            this.disableEmailField = false;
            this.disablePhoneField = false;
          });
      } else {
        this.resetResponsibleData();

        this.disableNameField = true;
        this.disableEmailField = true;
        this.disablePhoneField = true;
      }
    },

    'data.isDigital'() {
      this.franchiseDigitalCourses = [];
      this.franchiseGenericCourses = [];

      this.data.courses = [
        {
          genericId: '',
          id: '',
          franchiseNormalCourses: [],
        },
      ];
    },

    'data.student.taxpayerId'(value) {
      if (this.data.isResponsible && this.hasTaxpayerUserFetch(value)) {
        this.$store
          .dispatch('getPersonByTaxpayerId', {
            document: value,
          })
          .then((res) => {
            this.data.student.name = res.person.NomePessoaNomeFantasia;
            this.data.student.birthDate =
              moment(res.person.DataNascDataAbertura, 'YYYY-MM-DD').toDate() ??
              '';
            this.data.student.document = res.person.RGouIE ?? '';
          })
          .catch(() => {
            this.disableStudentNameField = false;
          });
      } else {
        this.data.student.name = '';

        this.disableStudentNameField = true;
      }
    },

    'data.franchiseId'(value) {
      this.toggleModality('normal');
      this.showModality = value != '653';

      if (!this.showModality) {
        this.toggleModality('digital');
      } else {
        this.enableToggleCards();
        this.fetchCourses();
      }
    },

    'data.startDate'(value) {
      this.disableEndDate = false;

      if (value == '' || value == null) {
        this.disableEndDate = true;
        this.data.endDate = '';

        this.$refs.endDateField.resetField();

        this.resetTimesField();
      } else {
        this.data.startWeekday = this.$service.capitalizeFirstLetter(
          moment(value).format('dddd')
        );
      }
    },

    'data.endDate'(value) {
      this.startTimesArr = this.createTimeArr();
      this.disableStartTime = false;

      if (value == '' || value == null) {
        this.resetTimesField();
      }
    },

    'data.startTime'(value) {
      this.endTimesArr = this.createTimeArr(value, '21:00', true);
      this.disableEndTime = false;
    },

    'data.payment.amount'(value) {
      if (value && value > 0) {
        this.disableCourseDiscountAmountField = false;
      } else {
        this.disableCourseDiscountAmountField = true;
        this.$refs.courseDiscountField.resetField();
        this.disableCourseInstallment = true;
      }
    },

    'data.payment.amountWithDiscount'(value) {
      if (value && value > 0) {
        this.disableCourseInstallment = false;
      }

      this.totalCourseAmount = this.formatLabelAmount(
        this.data.payment.installments,
        value
      );
    },

    'data.payment.installments'(value) {
      this.totalCourseAmount = this.formatLabelAmount(
        value,
        this.data.payment.amountWithDiscount
      );
    },

    'data.courseware.amount'(value) {
      if (value && value > 0) {
        this.disableCoursewareDiscountAmountField = false;
      } else {
        this.disableCoursewareDiscountAmountField = true;
        this.$refs.coursewareDiscountField.resetField();
        this.disableCoursewareInstallment = true;
      }
    },

    'data.courseware.amountWithDiscount'(value) {
      if (value && value > 0) {
        this.disableCoursewareInstallment = false;
      }

      this.totalCoursewareAmount = this.formatLabelAmount(
        this.data.courseware.installments,
        value
      );
    },

    'data.courseware.installments'(value) {
      this.totalCoursewareAmount = this.formatLabelAmount(
        value,
        this.data.courseware.amountWithDiscount
      );
    },

    'data.isTotalPayment'(value) {
      if (this.data.signal.amount != '') {
        this.data.hasSignal = !value;
      }
    },
  },

  methods: {
    formatFranchiseCourses(data) {
      data.franchiseNormalCourses = [];

      if (data.genericId) {
        this.$store
          .dispatch('getCourses', {
            generic_id: data.genericId,
            franchise_id: this.data.franchiseId,
            limit_items: 5000,
          })
          .then((res) => {
            data.franchiseNormalCourses = this.parseCoursesData(res.items);
          })
          .catch(() => {
            this.alertCardData = {
              text: 'Erro ao carregar os cursos da franquia',
              color: '#FF5A5A',
            };

            this.showAlertCard = true;
          });
      }
    },

    addCourse() {
      this.data.courses.push({
        id: '',
        genericId: '',
        franchiseNormalCourses: [],
      });
    },

    removeCourse(index) {
      this.data.courses.splice(index, 1);
    },

    hasCourseIdEmpty() {
      return !!this.data.courses.find((item) => item.id == '');
    },

    hasSomeCourseIdSelected() {
      return !!this.data.courses.find((item) => item.id != '');
    },

    fetchCourses() {
      if (!this.isDigital() && this.data.franchiseId) {
        this.fetchGenericCoursesByFranchise(this.data.franchiseId);
      } else {
        this.fetchCoursesDigital();
      }
    },

    hasTaxpayerUserFetch(value) {
      return (
        this.$service.validateTaxpayerId(value) &&
        this.$service.removeSpecialCharacter(value).length == 11
      );
    },

    resetResponsibleData() {
      this.data.name = '';
      this.data.phone = '';
      this.data.email = '';
      this.data.birthDate = '';
      this.data.document = '';
    },

    async fetchGenericCoursesByFranchise(franchiseId) {
      this.franchiseGenericCourses = [];

      await this.$store
        .dispatch('getCoursesByFranchise', {
          franchise_id: franchiseId,
          limit_items: 5000,
        })
        .then((res) => {
          this.franchiseGenericCourses = this.parseCoursesData(res.items);
        })
        .catch(() => {
          this.alertCardData = {
            text: 'Erro ao carregar os cursos genéricos da franquia',
            color: '#FF5A5A',
          };

          this.showAlertCard = true;
        });
    },

    async fetchMedias() {
      await this.$store
        .dispatch('getMedias')
        .then((res) => {
          const { items } = res;

          this.medias = items.map((item) => {
            return {
              label: item.Descricao,
              value: item.Codigo,
            };
          });
        })
        .catch(() => {
          this.alertCardData = {
            text: 'Erro ao buscar mídias',
            color: '#FF5A5A',
          };

          this.showAlertCard = true;
        });
    },

    async fetchServiceTypes() {
      await this.$store
        .dispatch('getContractServiceType')
        .then((res) => {
          const { items } = res;

          this.serviceTypes = items.map((item) => {
            return {
              label: item.Descricao,
              value: item.Codigo,
            };
          });
        })
        .catch(() => {
          this.alertCardData = {
            text: 'Erro ao buscar tipos de atendimento',
            color: '#FF5A5A',
          };

          this.showAlertCard = true;
        });
    },

    async fetchCoursesDigital() {
      this.franchiseDigitalCourses = [];

      await this.$store
        .dispatch('getCoursesDigital', {
          limit_items: 5000,
        })
        .then((res) => {
          this.franchiseDigitalCourses = this.parseCoursesData(res.items);
        })
        .catch(() => {
          this.alertCardData = {
            text: 'Erro ao carregar os cursos IM Digital da franquia',
            color: '#FF5A5A',
          };

          this.showAlertCard = true;
        });
    },

    submit() {
      this.isSummary = !this.isSummary;
      window.scrollTo(0, 0);
    },

    async createCheckout() {
      this.hasRequest = true;

      try {
        this.$androidService.sendMessage('im-contract-creation-started', {
          screen: 'newPOS',
          userColaborador: this.getCollaboratorId,
          documentsCount: this.data.courses.length,
        });
      } catch (error) {
        // Falha silenciosa em evento de telemetria
      }

      var courses = this.getSelectedCoursesDetail();

      var parsedCourses = courses.map((item) => {
        return {
          id: item.value,
          description: item.label,
        };
      });

      const selectedFranchise = this.getFranchisesCollaborator.find(
        (item) => item.value == this.data.franchiseId
      );

      var contractData = {
        data: {
          name: this.data.name,
          document: this.data.taxpayerId,
          telephone: this.data.phone,
          email: this.data.email,
          birthdate: this.formatDateForEvent(this.data.birthDate),
          rg: this.data.document ?? undefined,
          enrollment_fee: this.data.amountEnrollment,
          due_date: this.formatDateForEvent(this.data.dueDate),
          collaborator_id: this.getCollaboratorId,
          franchise_id: this.data.franchiseId,
          franchise_city_id: selectedFranchise?.franchiseCityId ?? undefined,
          is_digital: Number(this.data.isDigital),
          media_id: this.data.mediaId ?? 99,
          service_code_contract: this.data.serviceTypeId ?? 1,
        },

        address: {
          zipcode: this.data.postalCode,
          street: this.data.address.line1,
          district: this.data.address.neighborhood,
          city: this.data.address.city,
          state: this.data.address.stateCode,
        },

        courses: parsedCourses,

        is_total_payment: Number(this.data.isTotalPayment),

        payment: {
          value: this.data.payment.amount,
          value_with_discount: this.data.payment.amountWithDiscount,
          due_date: this.formatDateForEvent(this.data.payment.dueDate),
          installments: this.data.payment.installments,
        },
      };

      if (!this.data.isDigital) {
        contractData.data.start_weekday = this.data.startWeekday;
        contractData.data.start_date = this.formatDateForEvent(
          this.data.startDate
        );
        contractData.data.end_date = this.formatDateForEvent(this.data.endDate);
        contractData.data.start_time = this.data.startTime;
        contractData.data.end_time = this.data.endTime;
      }

      if (this.data.isResponsible) {
        contractData.is_responsible = 1;
        contractData.student = {
          name: this.data.student.name,
          document: this.data.student.taxpayerId,
          birthdate: this.formatDateForEvent(this.data.student.birthDate),
          rg: this.data.student.document ?? undefined,
        };
      }

      if (this.data.hasSignal) {
        contractData.is_signal = 1;
        contractData.signal = {
          value: this.data.signal.amount,
          due_date: this.formatDateForEvent(this.data.signal.dueDate),
        };
      }

      if (this.data.isCourseware) {
        contractData.is_courseware = 1;
        contractData.courseware = {};
        contractData.courseware.payment = {
          value: this.data.courseware.amount,
          value_with_discount: this.data.courseware.amountWithDiscount,
          due_date: this.formatDateForEvent(this.data.courseware.dueDate),
          installments: this.data.courseware.installments,
        };
      }

      try {
        const res = await this.$store.dispatch(
          'postCreateContract',
          contractData
        );

        const signUrl = res.contract.linkAluno;

        try {
          const qrCodeDataUrl = await QRCode.toDataURL(signUrl, {
            errorCorrectionLevel: 'H',
            type: 'image/png',
            quality: 0.95,
            margin: 2,
            width: 300,
          });

          this.contractCreatedData = {
            ...res.contract,
            qrCode: qrCodeDataUrl,
          };
        } catch (error) {
          this.contractCreatedData = res.contract;
        }

        try {
          const eventData = {
            screen: 'newPOS',
            timestamp: new Date().toISOString(),

            contract: {
              id: res.contract.Codigo,
              signUrl: signUrl,
              linkAluno: res.contract.linkAluno,
            },

            personal: {
              document: this.data.taxpayerId,
              name: this.data.name,
              email: this.data.email,
              phone: this.data.phone,
              birthDate: this.formatDateForEvent(this.data.birthDate),
              rg: this.data.document ?? null,
            },

            courses: this.getSelectedCoursesDetail().map((course) => ({
              id: course.value,
              description: course.label,
            })),

            payment: {
              baseValue: this.data.payment.amount,
              valueWithDiscount: this.data.payment.amountWithDiscount,
              dueDate: this.formatDateForEvent(this.data.payment.dueDate),
              installments: this.data.payment.installments,
              totalAmount: this.parseTotalCourseAmount(),
            },

            ...(this.data.isResponsible && {
              student: {
                name: this.data.student.name,
                document: this.data.student.taxpayerId,
                birthDate: this.formatDateForEvent(this.data.student.birthDate),
                rg: this.data.student.document ?? null,
              },
            }),
          };

          this.$androidService.sendMessage(
            'im-contract-created-successfully',
            eventData
          );
        } catch (error) {
          // Falha silenciosa em evento de telemetria
        }

        this.isSummary = false;
        this.isSuccess = true;
        window.scrollTo(0, 0);
      } catch (error) {
        try {
          this.$androidService.sendMessage('im-contract-created-error', {
            screen: 'newPOS',
            timestamp: new Date().toISOString(),
            userDocument: this.data.taxpayerId,
            userName: this.data.name,
            userEmail: this.data.email,
            franchiseId: this.data.franchiseId,
            collaboratorId: this.getCollaboratorId,
            errorMessage: error?.message || 'Erro ao criar contrato',
            errorDetails: error,
          });
        } catch (err) {
          // Falha silenciosa em evento de telemetria
        }

        this.alertCardData = {
          text: 'Erro ao criar contrato. Tente novamente!',
          color: '#FF5A5A',
        };

        this.showAlertCard = true;
      } finally {
        this.hasRequest = false;
      }
    },

    formatLabelAmount(installment, value) {
      return (
        'Total: ' +
        this.$service.formatBrlAmountSymbol(installment * value ?? 0)
      );
    },

    async fetchPostalCode() {
      if (!this.hasPostalCodeRequest) {
        this.hasPostalCodeRequest = true;

        await this.$store
          .dispatch(
            'getPostalCode',
            this.$service.removeSpecialCharacter(this.data.postalCode)
          )
          .then((res) => {
            this.data.address.line1 = res.address_line_1;
            this.data.address.city = res.city;
            this.data.address.neighborhood = res.neighborhood;
            this.data.address.stateCode = res.state_code;
          })
          .catch(() => {
            this.data.address = this.initialAddress();
            this.$refs.postalCode.inputButtonClick();

            this.alertCardData = {
              text: 'CEP inválido. Tente novamente!',
              color: '#FF5A5A',
            };

            this.showAlertCard = true;
          });

        this.hasPostalCodeRequest = false;
      }
    },

    initialAddress() {
      return {
        line1: '',
        city: '',
        neighborhood: '',
        stateCode: '',
      };
    },

    parseCoursesData(courses) {
      return courses.map((item) => ({
        label: item.Descricao,
        value: item.Codigo,
      }));
    },

    getMediaLabel(mediaId) {
      if (!mediaId || mediaId == 99) {
        return 'Pré - Matrícula';
      }

      var currentMediaData = this.medias.find((item) => item.value == mediaId);

      return currentMediaData.label;
    },

    getServiceTypeLabel(serviceTypeId) {
      if (!serviceTypeId) {
        return '-';
      }

      var currentServiceTypeData = this.serviceTypes.find(
        (item) => item.value == serviceTypeId
      );

      return currentServiceTypeData ? currentServiceTypeData.label : '-';
    },

    mountInputMoneyRules(param, min, max) {
      return 'required|notZero|' + param + ':' + min + ':' + max;
    },

    createTimeArr(start = '07:00', end = '21:00', setInterval = false) {
      var arr = [];
      var startTimeSplit = start.split(':');
      var startTime = parseInt(startTimeSplit[0]);
      var isStartHalfAnHour = parseInt(startTimeSplit[1]) == 30;

      var endTimeSplit = end.split(':');
      var endTime = parseInt(endTimeSplit[0]);

      var interval = endTime - startTime;

      for (var i = 0; i <= interval; i++) {
        var defaultTime = startTime + i;
        var currentTime = setInterval ? defaultTime + 1 : defaultTime;

        if ((i == 0 && !isStartHalfAnHour) || i != 0) {
          arr.push({
            label: currentTime + ':00',
            value: currentTime + ':00:00',
          });
        }

        if (i != interval) {
          arr.push({
            label: currentTime + ':30',
            value: currentTime + ':30:00',
          });
        }
      }

      return arr;
    },

    toggleModality(value) {
      var currentItem = this.modalityCardData.find(
        (item) => item.value == value
      );

      if (!currentItem.isActive || !currentItem.isDisabled) {
        this.modalityCardData.map((item) => (item.isActive = false));
        this.modalityCardData.find(
          (item) => item.value == value
        ).isActive = true;

        this.data.isDigital = this.isDigital();
        this.fetchCourses();
      }
    },

    resetTimesField() {
      this.disableStartTime = true;
      this.disableEndTime = true;

      this.data.startTime = '';
      this.data.endTime = '';
      this.data.startWeekday = '';
    },

    isDigital() {
      return this.modalityCardData.find((item) => item.value == 'digital')
        .isActive;
    },

    enableToggleCards() {
      this.modalityCardData.map((item) => (item.isDisabled = false));
    },

    disableToggleCards() {
      this.modalityCardData.map((item) => (item.isDisabled = true));
    },

    getSelectedCoursesDetail() {
      return this.data.courses.map((item) => {
        const matchingCourse = this.isDigital()
          ? this.franchiseDigitalCourses.find(
              (course) => course.value == item.id
            )
          : item.franchiseNormalCourses.find(
              (course) => course.value === item.id
            );

        return matchingCourse ? matchingCourse : [];
      });
    },

    parseDebitCourseAmount() {
      let totalAmount = this.parseTotalCourseAmount();

      totalAmount -= this.data.amountEnrollment;

      return totalAmount;
    },

    parseTime(time) {
      return time.split(':').slice(0, 2).join(':');
    },

    parseTotalCourseAmount() {
      let totalAmount = 0;

      totalAmount += this.data.amountEnrollment;
      totalAmount +=
        this.data.payment.amountWithDiscount * this.data.payment.installments;

      if (this.data.isCourseware) {
        totalAmount +=
          this.data.courseware.amountWithDiscount *
          this.data.courseware.installments;
      }

      return totalAmount;
    },

    getFranchiseLabel(franchiseId) {
      var currentFranchiseData = this.getFranchisesCollaborator.find(
        (item) => item.value == franchiseId
      );
      return franchiseId && currentFranchiseData
        ? currentFranchiseData.label
        : 'Selecione';
    },

    formatDateForEvent(dateValue) {
      if (!dateValue) return null;

      try {
        if (dateValue instanceof Date) {
          return moment(dateValue).format('YYYY-MM-DD');
        }

        if (typeof dateValue === 'string') {
          let parsedDate = moment(dateValue, 'DD/MM/YYYY', true);
          if (parsedDate.isValid()) {
            return parsedDate.format('YYYY-MM-DD');
          }

          parsedDate = moment(dateValue, 'YYYY-MM-DD', true);
          if (parsedDate.isValid()) {
            return parsedDate.format('YYYY-MM-DD');
          }

          parsedDate = moment(dateValue);
          if (parsedDate.isValid()) {
            return parsedDate.format('YYYY-MM-DD');
          }
        }

        return null;
      } catch (error) {
        return null;
      }
    },

    onStartNewContract() {
      this.isSuccess = false;
      this.contractCreatedData = null;
      this.isSummary = false;
      window.scrollTo(0, 0);

      Object.assign(this.$data, this.$options.data());

      const darkModeParam = this.$route.query?.darkMode;
      this.darkMode =
        typeof darkModeParam === 'boolean'
          ? darkModeParam
          : this.$service.stringToBoolean(darkModeParam);

      this.disableToggleCards();
    },

    onPaymentClick() {
      try {
        const contractData = {
          contractId: this.contractCreatedData?.Codigo ?? '',
          franchiseId: this.data.franchiseId ?? '',
          name: this.data.name ?? '',
          taxpayerId: this.data.taxpayerId ?? '',
          studentName: this.data.isResponsible
            ? this.data.student.name ?? ''
            : this.data.name ?? '',
          courseName: this.getSelectedCoursesDetail()[0]?.label ?? '',
        };

        const eventData = {
          screen: 'newPOS',
          timestamp: new Date().toISOString(),
          ...contractData,
        };

        this.$androidService.sendMessage(
          'im-contract-payment-click',
          eventData
        );
      } catch (error) {
        // Falha silenciosa em evento de telemetria
      }
    },

    toggleTotalPayment() {
      if (!this.hasRequest) {
        this.data.isTotalPayment = !this.data.isTotalPayment;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.container {
  display: flex;
  justify-content: center;

  .content {
    margin-top: 1.5rem;
    margin-bottom: 1.5rem;
    padding: 0 1.5rem;
    max-width: 672px;
    width: 100%;

    .form-container {
      form {
        width: 100%;
      }
    }

    .default-section-component {
      .field-container {
        .section {
          p {
            font-size: 12px;
            color: #6d6f6f;
            margin-top: 2rem;
            margin-bottom: 1.5rem;
            font-weight: 500;

            &.dark-mode {
              color: #ffffff;
            }
          }

          .input-field {
            width: 100%;
            margin-bottom: 1.5rem;
          }

          .range-field {
            width: 100%;
            margin-bottom: 1.5rem;
          }

          .row {
            display: flex;
            gap: 1.5rem;
            margin-bottom: 1.5rem;

            .input-field {
              flex: 1;
              min-width: 100px;
              margin-bottom: 0;
              margin-right: 0.75rem;

              &:last-child {
                margin-right: 0;
              }
            }
          }

          .checkbox-container {
            margin-bottom: 2rem;
          }

          .toggle-card-wrapper {
            margin-bottom: 1.5rem;

            .toggle-card {
              display: flex;
              flex-wrap: nowrap;

              .card {
                flex: 1;
                height: 48px;
                padding: 1rem;
                margin-right: 1.5rem;
                border-radius: 6px;
                border: 1px solid #ff8b00;
                background: transparent;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #ff8b00;
                cursor: pointer;
                text-align: center;
                font-size: 14px;
                transition: all 0.2s ease;

                &:last-child {
                  margin-right: 0;
                }
                word-wrap: break-word;

                &.disable {
                  cursor: not-allowed;
                  opacity: 0.5;
                }

                &.active {
                  border: 1px solid #ff8b00;
                  background: #ff8b00;
                  color: #fff;
                  opacity: 1;
                }
              }
            }

            &.dark-mode {
              .toggle-card .card {
                border: 1px solid #ff8b00;
                background: transparent;
                color: #ff8b00;

                &.disable {
                  opacity: 0.5;
                }

                &.active {
                  border: 1px solid #ff8b00;
                  background: #ff8b00;
                  color: #fff;
                }
              }
            }
          }

          .course-item {
            margin-bottom: 1.5rem;
          }

          .course-version-container {
            display: flex;
            gap: 1rem;
            align-items: flex-end;

            .input-field {
              flex: 1;
              margin-bottom: 0;
            }

            .remove-course-btn {
              height: 48px;
              width: 48px;
              min-width: 48px;
              border: none;
              background: transparent;
              cursor: pointer;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 0;
              margin-bottom: 0.5rem;

              i {
                color: #ff5a5a;
                font-size: 16px;
              }

              &:hover {
                opacity: 0.8;
              }
            }
          }
        }
      }

      .summary-container {
        margin-top: 1rem;
        font-weight: 300;

        .summary-section {
          margin-bottom: 2rem;

          .item {
            display: flex;
            margin-top: 0.5rem;

            p {
              font-size: 16px;
              color: #333;
            }

            &.action {
              flex-direction: unset;

              .toggle-switch-component {
                margin-left: auto;
              }
            }

            .label {
              font-weight: 400;
              margin-right: 2rem;
              color: #333;
            }

            .value {
              color: #333;
              margin-left: auto;
            }
          }

          .divider {
            height: 1px;
            background: #dadada;
            margin-top: 1rem;
            margin-bottom: 1rem;
          }
        }

        &.dark-mode {
          .summary-section {
            .item {
              p {
                color: #ffffff;
              }

              .label {
                color: #ffffff;
              }

              .value {
                color: #ffffff;
              }
            }

            .divider {
              background: #a0aab8;
            }
          }
        }
      }

      .success-container {
        margin-top: 1rem;

        .success-message {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;

          .contract-id {
            font-size: 16px;
            font-weight: 500;
            color: #333;
            margin-bottom: 1.2rem;

            span {
              color: #ff8b00;
              font-weight: 600;
            }
          }

          .qr-code-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 1rem;

            .qr-code-label {
              font-size: 16px;
              font-weight: 500;
              color: #666;
              margin: 0;
            }

            .qr-code-container {
              padding: 0.2rem;
              background: #ffffff;
              border-radius: 8px;
              margin-top: 1rem;
              margin-bottom: 1rem;

              .qr-code-image {
                max-width: 200px;
                width: 100%;
                height: auto;
              }
            }

            .qr-code-hint {
              font-size: 13px;
              color: #666;
              margin: 0.3rem;
              max-width: 400px;
              line-height: 1.4;
            }
          }
        }

        // Dark Mode Styles - Explicit overrides
        &.dark-mode .success-message .contract-id {
          color: #ffffff;

          span {
            color: #ff8b00;
          }
        }

        &.dark-mode .success-message .qr-code-section .qr-code-label {
          color: #a0aab8;
        }

        &.dark-mode .success-message .qr-code-section .qr-code-container {
          background: #1a1a1a;
          border: 1px solid #a0aab8;
        }

        &.dark-mode .success-message .qr-code-section .qr-code-hint {
          color: #7a8090;
        }
      }
    }

    .button-container {
      margin-top: 1rem;
      display: flex;
      justify-content: center;
      width: 100%;
      flex-wrap: wrap;

      .button-component {
        flex: 1;
        max-width: 200px;
        margin-right: 2.5rem;

        &.left {
          margin-left: auto;
        }

        &:last-child {
          margin-right: 0;
        }
      }

      &.success-buttons {
        flex-wrap: wrap;

        .button-component {
          flex: 1;
          max-width: 200px;
          margin-right: 2.5rem;

          &:last-child {
            margin-right: 0;
          }
        }
      }
    }
  }

  .alert-card-component {
    margin-top: 0.5rem;
  }
}

@media (max-width: 755px) {
  .container {
    .content {
      .form-container {
        form {
          width: 100%;
        }
      }

      .default-section-component {
        .field-container {
          .section {
            .toggle-card {
              .card {
                min-width: 100px;
                padding: 0.75rem;
                font-size: 13px;
                margin-right: 1rem;
              }

              .card:last-child {
                margin-right: 0;
              }
            }
          }
        }

        .summary-container {
          .item {
            flex-direction: column;
            margin-top: 1rem !important;

            p {
              font-size: 14px !important;
            }

            .value {
              margin-left: unset !important;
            }
          }
        }
      }
    }
  }
}

@media (max-width: 420px) {
  .container {
    .content {
      padding: 0 1rem;

      .form-container {
        form {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }
      }

      .default-section-component {
        .field-container {
          .section {
            .toggle-card {
              .card {
                padding: 0.75rem 0.5rem;
                font-size: 12px;
                height: 44px;
                margin-right: 0.75rem;
              }

              .card:last-child {
                margin-right: 0;
              }
            }
          }
        }
      }

      .button-container {
        .button-component {
          width: 100%;

          &.left {
            margin-left: unset;
          }
        }
      }
    }
  }
}
</style>
